import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MessageSquareIcon,
  SearchIcon,
  FilterIcon,
  BellIcon,
  PlusIcon,
  Loader2 as LoaderIcon,
  AlertCircleIcon,
} from "lucide-react";

import InquiryDetail, { Inquiry } from "@/polymet/components/inquiry-detail";
import { FileAttachment } from "@/polymet/components/file-attachment-preview";
import InquiryListItem from "@/polymet/components/inquiry-list-item";
import EmptyState from "@/polymet/components/empty-state";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import { RFQService } from "@/lib/rfq-service";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Map RFQ or inquiry from database to our Inquiry interface
const mapToInquiry = (rfqData: any, userType: 'buyer' | 'supplier'): Inquiry => {
  return {
    id: rfqData.id,
    subject: rfqData.title || 'Untitled Inquiry',
    status: (rfqData.status as Inquiry['status']) || 'pending',
    createdAt: rfqData.created_at || new Date().toISOString(),
    updatedAt: rfqData.updated_at || rfqData.created_at || new Date().toISOString(),
    product: {
      id: rfqData.product_id || 'unknown',
      name: rfqData.product_name || rfqData.title || 'Unknown Product',
      image: rfqData.product_image || 'https://placehold.co/600x400?text=No+Image',
    },
    seller: {
      id: rfqData.supplier_id || 'unknown',
      name: rfqData.supplier_name || 'Unknown Supplier',
      avatar: rfqData.supplier_avatar || 'https://placehold.co/50x50?text=S',
    },
    buyer: {
      id: rfqData.user_id || 'unknown',
      name: rfqData.user_name || 'Unknown Buyer',
      avatar: rfqData.user_avatar || 'https://placehold.co/50x50?text=B',
    },
    messages: [],
  }
};

// Initialize RFQ service
const rfqService = new RFQService();

export default function InquiryManagementPage() {
  const { inquiryId } = useParams<{ inquiryId: string }>(); // Get inquiry ID from URL if present
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [messageText, setMessageText] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReplying, setIsReplying] = useState(false);
  const [userType, setUserType] = useState<'buyer' | 'supplier' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("all");
  
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check authentication and load user data
  useEffect(() => {
    const checkAuth = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        // Get user profile type (buyer or supplier)
        const { data: profileData, error: profileError } = await supabase
          .from('accounts')
          .select('full_name, company_name, user_type')
          .eq('id', user.id)
          .single();
        
        if (profileError) {
          throw profileError;
        }
        
        if (profileData?.user_type === 'buyer' || profileData?.user_type === 'supplier' || profileData?.user_type === 'both') {
          setUserType(profileData.user_type);
        } else {
          setError('Your account type does not have access to inquiries.');
        }
      } catch (err) {
        console.error('Error checking user type:', err);
        setError('Failed to load user profile.');
      }
    };
    
    checkAuth();
  }, [user]);
  
  // Redirect unauthorized users
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login?redirect=/inquiries');
    }
  }, [loading, user, navigate]);
  
  // Load inquiries based on user type
  useEffect(() => {
    const fetchInquiries = async () => {
      if (!user || !userType) return;
      
      try {
        setLoading(true);
        let inquiriesData: any[] = [];
        
        if (userType === 'buyer') {
          // Get buyer's inquiries
          inquiriesData = await rfqService.getUserRFQs(user.id);
        } else if (userType === 'supplier') {
          // Get supplier's inquiries
          inquiriesData = await rfqService.getOpenRFQsForSupplier(user.id);
        }
        
        // Map the raw data to our Inquiry interface
        const mappedInquiries: Inquiry[] = inquiriesData.map(item => 
          mapToInquiry(item, userType)
        );
        
        setInquiries(mappedInquiries);
        
        // Load active inquiry if ID is in URL
        if (inquiryId) {
          const activeInq = mappedInquiries.find(inq => inq.id === inquiryId);
          if (activeInq) {
            setSelectedInquiry(activeInq);
          }
        }
      } catch (err) {
        console.error('Error fetching inquiries:', err);
        setError('Failed to load inquiries. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInquiries();
    
    // Set up real-time subscription to inquiries
    if (user) {
      const channel = supabase
        .channel('inquiries-changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'rfq_requests',
          filter: userType === 'buyer' 
            ? `user_id=eq.${user.id}` 
            : `supplier_id=eq.${user.id}`
        }, () => {
          fetchInquiries();
        })
        .subscribe();
      
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, userType, inquiryId]);

  // Find unread messages
  const unreadCount = inquiries.reduce((count, inquiry) => {
    const unreadMessages = (inquiry.messages || []).filter(
      (msg) => !msg.isRead && msg.sender?.id !== user?.id
    );
    return count + unreadMessages.length;
  }, 0);

  // Filter inquiries based on search query and status filter
  const filteredInquiries = inquiries.filter((inquiry) => {
    // Apply text search filter
    const matchesSearch =
      searchQuery === "" ||
      (inquiry.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      (inquiry.product?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      (inquiry.buyer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
      (inquiry.seller?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

    // Apply status filter
    const matchesStatus =
      statusFilter === "all" ||
      inquiry.status === statusFilter ||
      (statusFilter === "active" &&
        ["new", "pending", "responded"].includes(inquiry.status ?? '')) ||
      (statusFilter === "closed" &&
        ["closed", "expired"].includes(inquiry.status ?? ''));

    return matchesSearch && matchesStatus;
  });

  // Sort inquiries based on selected sort option
  const sortedInquiries = [...filteredInquiries].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    } else if (sortBy === "oldest") {
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    }
    return 0;
  });

  // Handle sending a message
  const handleSendMessage = async (
    inquiryId: string,
    message: string,
    attachments: FileAttachment[]
  ) => {
    if ((!message.trim() && attachments.length === 0) || !user) return;

    setIsReplying(true);
    setLoading(true);

    try {
      // Find the inquiry we're replying to
      const inquiryIndex = inquiries.findIndex((inq) => inq.id === inquiryId);
      if (inquiryIndex === -1) return;

      // Create a copy of the inquiries array
      const updatedInquiries = [...inquiries];
      const inquiry = { ...updatedInquiries[inquiryIndex] };
      
      // Determine sender role based on user type
      const senderRole = userType === 'buyer' ? 'buyer' as const : 'seller' as const;
      
      // Store message in Supabase
      const { data: messageData, error: messageError } = await supabase
        .from('inquiry_messages')
        .insert({
          inquiry_id: inquiryId,
          user_id: user.id,
          content: message,
          user_role: senderRole,
          attachments: attachments.length > 0 ? attachments.map(a => ({ 
            name: a.name, 
            size: a.size, 
            type: a.type 
          })) : null
        })
        .select();
      
      if (messageError) throw messageError;
      
      // Update inquiry status if needed
      if (inquiry.status === "new" || inquiry.status === "pending") {
        // Update the inquiry status in the database
        const newStatus = userType === 'buyer' ? 'pending' : 'responded';
        
        // Update status in database
        try {
          await supabase
            .from('rfq_requests')
            .update({ status: newStatus })
            .eq('id', inquiryId);
          
          // Update local inquiry status
          inquiry.status = newStatus as Inquiry['status'];
          inquiry.updatedAt = new Date().toISOString();
        } catch (statusError) {
          console.error('Error updating status:', statusError);
        }
      }
      
      // Create a new message for local state
      const newMessage = {
        id: messageData?.[0]?.id || `msg-${Date.now()}`,
        content: message,
        sender: {
          id: user.id,
          name: userType === 'buyer' ? inquiry.buyer.name : inquiry.seller.name,
          avatar: userType === 'buyer' ? inquiry.buyer.avatar : inquiry.seller.avatar,
          role: senderRole,
        },
        timestamp: new Date().toISOString(),
        attachments: attachments.length > 0 ? [...attachments] : undefined,
        isRead: true,
      };
      
      // Add the new message to the inquiry
      if (!inquiry.messages) inquiry.messages = [];
      inquiry.messages.push(newMessage);
      
      // Update the inquiry in the array
      updatedInquiries[inquiryIndex] = inquiry;
      
      // Update the state
      setInquiries(updatedInquiries);
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(inquiry);
      }
      
      // Clear the message input
      setMessageText("");
      setAttachments([]);
      
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
      setIsReplying(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (
    inquiryId: string,
    newStatus: Inquiry["status"]
  ) => {
    if (!user) return;
    
    setLoading(true);

    try {
      // Find the inquiry we're updating
      const inquiryIndex = inquiries.findIndex((inq) => inq.id === inquiryId);
      if (inquiryIndex === -1) return;

      // Create a copy of the inquiries array
      const updatedInquiries = [...inquiries];
      const inquiry = { ...updatedInquiries[inquiryIndex] };

      // Update the inquiry status in the database
      try {
        await supabase
          .from('rfq_requests')
          .update({ status: newStatus })
          .eq('id', inquiryId);
      } catch (error) {
        console.error('Error updating status in database:', error);
        throw error;
      }
      
      // Record status change in the message history
      const { error: messageError } = await supabase
        .from('inquiry_messages')
        .insert({
          inquiry_id: inquiryId,
          user_id: 'system',
          content: `Status changed to '${newStatus}'`,
          user_role: 'system',
        });
      
      if (messageError) throw messageError;

      // Update the inquiry status in local state
      inquiry.status = newStatus;
      inquiry.updatedAt = new Date().toISOString();

      // Add a status change message to the local state
      const statusMessage = {
        id: `msg-${Date.now()}`,
        content: `Inquiry status changed to '${newStatus}'`,
        sender: {
          id: "system",
          name: "System",
          role: "system",
        },
        timestamp: new Date().toISOString(),
        isRead: true,
      };

      // Add the status message to the inquiry
      if (!inquiry.messages) inquiry.messages = [];
      inquiry.messages.push(statusMessage);

      // Update the inquiry in the array
      updatedInquiries[inquiryIndex] = inquiry;

      // Update the state
      setInquiries(updatedInquiries);
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(inquiry);
      }
    } catch (error) {
      console.error("Error updating inquiry status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle marking a message as read
  const handleMessageRead = async (messageId: string) => {
    if (!selectedInquiry) return;

    try {
      // Update the message in the database
      await supabase
        .from('inquiry_messages')
        .update({ is_read: true })
        .eq('id', messageId);

      // Update the message in our local state
      const updatedInquiry = { ...selectedInquiry };
      if (updatedInquiry.messages) {
        updatedInquiry.messages = updatedInquiry.messages.map((msg) =>
          msg.id === messageId ? { ...msg, isRead: true } : msg
        );
      }

      setSelectedInquiry(updatedInquiry);

      // Also update the inquiry in the inquiries array
      setInquiries(
        inquiries.map((inq) => (inq.id === updatedInquiry.id ? updatedInquiry : inq))
      );
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  // Check if an inquiry has unread messages
  const hasUnreadMessages = (inquiry: Inquiry) => {
    return (
      inquiry.messages?.some((msg) => !msg.isRead && msg.sender?.id !== user?.id) || false
    );
  };

  // Get the last message for an inquiry
  const getLastMessage = (inquiry: Inquiry) => {
    if (!inquiry.messages || inquiry.messages.length === 0) return null;
    return inquiry.messages[inquiry.messages.length - 1];
  };

  // Handle creating a new inquiry
  const handleNewInquiry = () => {
    navigate("/new-inquiry");
  };

  if (loading && inquiries.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[60vh]">
        <LoaderIcon className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading inquiries...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <Button onClick={() => navigate('/')} className="mt-4">
          Go to Homepage
        </Button>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Please log in to view inquiries</h2>
          <p className="text-muted-foreground mb-6">You need to be logged in to access your inquiries.</p>
          <Button onClick={() => navigate('/login')}>Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {selectedInquiry ? (
        <InquiryDetail
          inquiry={selectedInquiry}
          onBack={() => setSelectedInquiry(null)}
          onSendMessage={(message, attachments) =>
            handleSendMessage(selectedInquiry.id, message, attachments)
          }
          onStatusChange={(newStatus) =>
            handleStatusChange(selectedInquiry.id, newStatus)
          }
          onMessageRead={handleMessageRead}
          currentUserId={user.id}
          isReplying={isReplying}
        />
      ) : (
        <Card>
          <CardHeader className="flex flex-col space-y-2 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div>
              <CardTitle>Inquiries</CardTitle>
              <CardDescription>
                View and manage your inquiries
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="relative"
                onClick={() => {/* Toggle notifications */}}
              >
                <BellIcon className="h-4 w-4" />
                {unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -right-2 -top-2 h-5 w-5 p-0 text-xs flex items-center justify-center"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>
              <Button onClick={handleNewInquiry}>
                <PlusIcon className="mr-2 h-4 w-4" /> New Inquiry
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col space-y-2 md:flex-row md:items-center md:space-x-2 md:space-y-0">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search inquiries..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="responded">Responded</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="newest">Newest</SelectItem>
                        <SelectItem value="oldest">Oldest</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="More filters"
                    aria-label="More filters"
                  >
                    <FilterIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="closed">Closed</TabsTrigger>
                </TabsList>
                <TabsContent value="all">
                  {sortedInquiries.length > 0 ? (
                    <div className="space-y-4">
                      {sortedInquiries.map((inquiry) => (
                        <InquiryListItem
                          key={inquiry.id}
                          id={inquiry.id}
                          subject={inquiry.subject}
                          status={inquiry.status}
                          createdAt={inquiry.createdAt}
                          updatedAt={inquiry.updatedAt}
                          productImage={inquiry.product.image}
                          productName={inquiry.product.name}
                          buyerName={inquiry.buyer.name}
                          sellerName={inquiry.seller.name}
                          currentUserId={user.id}
                          buyerId={inquiry.buyer.id}
                          sellerId={inquiry.seller.id}
                          hasUnreadMessages={hasUnreadMessages(inquiry)}
                          lastMessagePreview={getLastMessage(inquiry)?.content}
                          onClick={() => setSelectedInquiry(inquiry)}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      title="No inquiries found"
                      description={
                        searchQuery || statusFilter !== "all"
                          ? "Try adjusting your filters"
                          : "You don't have any inquiries yet"
                      }
                    />
                  )}
                </TabsContent>
                <TabsContent value="active">
                  {/* Active inquiries filtered further */}
                  {sortedInquiries.filter(inq => 
                    ["new", "pending", "responded"].includes(inq.status)
                  ).length > 0 ? (
                    <div className="space-y-4">
                      {sortedInquiries
                        .filter(inq => ["new", "pending", "responded"].includes(inq.status))
                        .map((inquiry) => (
                          <InquiryListItem
                            key={inquiry.id}
                            id={inquiry.id}
                            subject={inquiry.subject}
                            status={inquiry.status}
                            createdAt={inquiry.createdAt}
                            updatedAt={inquiry.updatedAt}
                            productImage={inquiry.product.image}
                            productName={inquiry.product.name}
                            buyerName={inquiry.buyer.name}
                            sellerName={inquiry.seller.name}
                            currentUserId={user.id}
                            buyerId={inquiry.buyer.id}
                            sellerId={inquiry.seller.id}
                            hasUnreadMessages={hasUnreadMessages(inquiry)}
                            lastMessagePreview={getLastMessage(inquiry)?.content}
                            onClick={() => setSelectedInquiry(inquiry)}
                          />
                        ))}
                    </div>
                  ) : (
                    <EmptyState
                      title="No active inquiries"
                      description="You don't have any active inquiries at the moment"
                    />
                  )}
                </TabsContent>
                <TabsContent value="closed">
                  {/* Closed inquiries filtered further */}
                  {sortedInquiries.filter(inq => 
                    ["closed", "expired"].includes(inq.status)
                  ).length > 0 ? (
                    <div className="space-y-4">
                      {sortedInquiries
                        .filter(inq => ["closed", "expired"].includes(inq.status))
                        .map((inquiry) => (
                          <InquiryListItem
                            key={inquiry.id}
                            id={inquiry.id}
                            subject={inquiry.subject}
                            status={inquiry.status}
                            createdAt={inquiry.createdAt}
                            updatedAt={inquiry.updatedAt}
                            productImage={inquiry.product.image}
                            productName={inquiry.product.name}
                            buyerName={inquiry.buyer.name}
                            sellerName={inquiry.seller.name}
                            currentUserId={user.id}
                            buyerId={inquiry.buyer.id}
                            sellerId={inquiry.seller.id}
                            hasUnreadMessages={hasUnreadMessages(inquiry)}
                            lastMessagePreview={getLastMessage(inquiry)?.content}
                            onClick={() => setSelectedInquiry(inquiry)}
                          />
                        ))}
                    </div>
                  ) : (
                    <EmptyState
                      title="No closed inquiries"
                      description="You don't have any closed or expired inquiries"
                    />
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
